import { useEffect, useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { message, Modal, Select } from "antd";
import { Region, Zone } from "../../components/modals/CreateWarehouseModal";
import {
  useAssignUserToWarehouse,
  useCountryStruct,
  useGetAllActiveAgronomists,
  useGetWorkLocations,
} from "../../api/admin";
import { z } from "zod";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTablePrint } from "../../hooks/useTablePrint";
import { WorkLocation } from "../../types/workLocation";
import { Input } from "../../components/ui/input";
import { Icons } from "../../assets/images/icons";
import { AppButton } from "../../components/ui/AppButton";
import { ExportButton } from "../../components/ui/ExportButton";
import { DataTable } from "../../components/ui/data-table";
import { getStatusColor } from "../../util/status-formatter";

function WorkLocationPage() {
  /** schema for work location definition */
  const WorkLocationSchema = z.object({
    selectedUserId: z.string().min(1, { message: "User is required" }),
    selectedRegionId: z.string().min(1, { message: "Region is required" }),
    selectedZoneId: z.string().min(1, { message: "Zone is required" }),
  });

  type WorkLocationForm = z.infer<typeof WorkLocationSchema>;

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [openWorkLocation, setOpenWorkLocation] = useState(false);
  const [zones, setZones] = useState<Zone[] | undefined>();
  const [regions, setRegions] = useState<Region[] | undefined>();

  /** API */
  const { data: workLocations, isLoading } = useGetWorkLocations({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();
  const { data: agronomists } = useGetAllActiveAgronomists();
  const { data } = useCountryStruct();
  const { mutateAsync: assignUser, isPending: assigningUser } =
    useAssignUserToWarehouse();

  /** Transform work locations for table */
  const transformedWorkLocations = useMemo((): WorkLocation[] => {
    if (!workLocations?.data?.content) return [];
    return workLocations.data.content.map((item: WorkLocation) => ({
      id: item.id,
      fullName: item.fullName,
      phoneNumber: item.phoneNo,
      email: item.email,
      region: item.region,
      zone: item.zone,
      status: item.status,
    }));
  }, [workLocations]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<WorkLocationForm>({
    resolver: zodResolver(WorkLocationSchema),
    defaultValues: {
      selectedUserId: "",
      selectedRegionId: "",
      selectedZoneId: "",
    },
    mode: "onSubmit",
  });

  const selectedRegionId = watch("selectedRegionId");

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  /** Columns */
  const workLocationColumns = [
    {
      key: "fullName",
      title: "Full Name",
      dataIndex: "fullName",
      align: "left" as const,
    },
    {
      key: "phoneNumber",
      title: "Phone Number",
      dataIndex: "phoneNumber",
      align: "left" as const,
    },
    {
      key: "email",
      title: "Email",
      dataIndex: "email",
      align: "left" as const,
    },
    {
      key: "region",
      title: "Region",
      dataIndex: "region",
      align: "left" as const,
    },
    {
      key: "zone",
      title: "Zone",
      dataIndex: "zone",
      align: "left" as const,
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      align: "left" as const,
      render: (value: string) => (
        <div
          className="px-3 py-1 max-w-[85px] rounded-md text-center text-xs font-medium capitalize"
          style={{
            color: getStatusColor(value, "text"),
            backgroundColor: getStatusColor(value, "bg"),
          }}
        >
          {value?.charAt(0).toUpperCase() + value?.slice(1).toLowerCase()}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (data && data.data) {
      const _regions = data.data.data.regionDtoList.map((item: any) => ({
        regionId: item.regionId.toString(),
        regionName: item.regionName,
      }));
      setRegions(_regions);
    }
  }, [data]);

  useEffect(() => {
    if (data && data.data && selectedRegionId) {
      const region = data.data.data.regionDtoList.find(
        (r: any) => r.regionId.toString() === selectedRegionId
      );
      if (region) {
        const _zones = region.zoneDtos.map((item: any) => ({
          zoneId: item.zoneId.toString(),
          zoneName: item.zoneName,
        }));
        setZones(_zones);
      }
    }
  }, [selectedRegionId, data]);

  const addWorkLocation: SubmitHandler<WorkLocationForm> = async (values) => {
    try {
      await assignUser({
        userId: Number(values.selectedUserId),
        regionId: Number(values.selectedRegionId),
        zoneId: Number(values.selectedZoneId),
      });
      message.success("Work location assigned successfully.");
      setOpenWorkLocation(false);
      reset();
    } catch (err) {
      message.error("Operation failed");
    }
  };

  return (
    <div
      className="bg-transparent lg:bg-white"
      style={{ borderRadius: 8.5, marginTop: 25 }}
    >
      <div className="flex flex-col sm:flex-row justify-between">
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search work locations by name"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(
                workLocationColumns,
                transformedWorkLocations,
                "Work Location Report"
              )
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={workLocationColumns}
            data={transformedWorkLocations}
            fileName="workLocations"
            title="Work Location Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: workLocations?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />

          <AppButton
            customIcon={Icons.addCircle}
            variant="success"
            onClick={() => setOpenWorkLocation(true)}
          >
            Create New
          </AppButton>
        </div>
      </div>

      {/** Desktop Table */}
      <div className="hidden md:block">
        <DataTable<WorkLocation>
          data={transformedWorkLocations}
          columns={workLocationColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: workLocations?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No work locations found"
          rowKey="id"
        />
      </div>

      <Modal
        title={<div style={{ color: "black" }}>Add Work Location</div>}
        open={openWorkLocation}
        centered
        onOk={handleSubmit(addWorkLocation)}
        okButtonProps={{
          disabled: assigningUser,
          loading: assigningUser,
        }}
        onCancel={() => setOpenWorkLocation(false)}
        closable
        style={{ color: "black" }}
      >
        <Controller
          name="selectedUserId"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              className="mb-3"
              placeholder="Select user"
              style={{ width: "100%" }}
              options={agronomists?.data?.map((item: any) => ({
                label: item.fullName,
                value: String(item.id),
              }))}
              value={field.value ? String(field.value) : undefined}
              onChange={(value) => field.onChange(value)}
            />
          )}
        />
        {errors.selectedRegionId && (
          <span style={{ color: "red" }}>
            {errors.selectedRegionId.message}
          </span>
        )}

        <Controller
          name="selectedRegionId"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              className="mb-3"
              placeholder="Select region"
              style={{ width: "100%" }}
              options={regions?.map((item: any) => ({
                label: item.regionName,
                value: String(item.regionId), // ensure it's a string
              }))}
              value={field.value ? String(field.value) : undefined} // normalize
              onChange={(value) => field.onChange(value)} // pass back as string
            />
          )}
        />
        {errors.selectedRegionId && (
          <span style={{ color: "red" }}>
            {errors.selectedRegionId.message}
          </span>
        )}

        <Controller
          name="selectedZoneId"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              placeholder="Select zone"
              style={{ width: "100%" }}
              options={zones?.map((_zone: any) => ({
                label: _zone.zoneName,
                value: _zone.zoneId,
              }))}
              value={field.value || undefined}
            />
          )}
        />
        {errors.selectedZoneId && (
          <span style={{ color: "red" }}>{errors.selectedZoneId.message}</span>
        )}
      </Modal>
    </div>
  );
}

export default WithAuth(WorkLocationPage);
